import type { TreeReplacementAccess, TreeReplacementTarget } from './types';

/** A failed tree-replacement request, carrying its HTTP status so the screen can say
 *  what actually went wrong. */
export class TreeReplacementError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Per-field messages from a 400, keyed by request field name. */
    readonly fieldErrors: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'TreeReplacementError';
  }
}

function fallbackMessage(
  status: number,
  target: TreeReplacementTarget,
  access: TreeReplacementAccess,
): string {
  switch (status) {
    case 400:
      return 'Some of the details aren’t valid. Check them and try again.';
    case 403:
      return access === 'view'
        ? 'You don’t have access to this workshop’s tree replacements.'
        : 'You don’t have permission to manage tree replacements for this workshop.';
    case 404:
      return target === 'workshop'
        ? 'This workshop no longer exists.'
        : 'This replacement no longer exists. Reload the page to see the current list.';
    default:
      return 'Something went wrong. Try again in a moment.';
  }
}

/** Keys may arrive as paths such as `body.email`; the field name is the last part. */
function fieldErrorsByName(raw: Record<string, string> | undefined): Record<string, string> {
  return Object.fromEntries(
    Object.entries(raw ?? {}).map(([path, message]) => [path.split('.').at(-1) ?? path, message]),
  );
}

/** Reads a failed response and throws it as a `TreeReplacementError`. */
export async function throwTreeReplacementError(
  res: Response,
  target: TreeReplacementTarget,
  access: TreeReplacementAccess = 'manage',
): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as {
    message?: string;
    fieldErrors?: Record<string, string>;
  };
  const message = res.status === 400 && body.message ? body.message : undefined;
  throw new TreeReplacementError(
    message ?? fallbackMessage(res.status, target, access),
    res.status,
    res.status === 400 ? fieldErrorsByName(body.fieldErrors) : {},
  );
}
